import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { LocalizationService } from '../../core/localization/localization.service';
import { FinancialAccountSelectorComponent } from '../../shared/design-system/financial-account-selector.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { BankRecord, CreateBankPayload, FinanceApiService, UpdateBankPayload } from './finance-api.service';

@Component({
  standalone: true,
  selector: 'ain-finance-bank-detail',
  imports: [AinPageComponent, AinStateComponent, RouterLink, FormsModule, ButtonModule, InputTextModule, FinancialAccountSelectorComponent],
  template: `
    <ain-page [title]="isCreateMode() ? t('finance.banks.form.createTitle') : t('finance.banks.form.editTitle')" [description]="t('finance.banks.description')">
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else {
        <div class="toolbar">
          <a class="back-link" [routerLink]="['/financial/payments/banks']">{{ t('catalog.action.backToList') }}</a>
          <button pButton type="button" [label]="t('common.save')" [disabled]="saving()" (click)="save()"></button>
        </div>

        <section class="card">
          <div class="grid">
            <label>
              <span>{{ t('finance.banks.fields.bankName') }}</span>
              <input pInputText type="text" [(ngModel)]="form.bankName" />
            </label>
            <label>
              <span>{{ t('finance.banks.fields.branch') }}</span>
              <input pInputText type="text" [(ngModel)]="form.branch" />
            </label>
            <label>
              <span>{{ t('finance.banks.fields.accountType') }}</span>
              <input pInputText type="text" [(ngModel)]="form.accountType" />
            </label>
            <label>
              <span>{{ t('finance.banks.fields.accountNumber') }}</span>
              <input pInputText type="text" [(ngModel)]="form.accountNumber" />
            </label>
            <label>
              <span>{{ t('finance.banks.fields.iban') }}</span>
              <input pInputText type="text" [(ngModel)]="form.iban" />
            </label>
            <label>
              <span>{{ t('finance.banks.fields.cardNumber') }}</span>
              <input pInputText type="text" [(ngModel)]="form.cardNumber" />
            </label>
            <label class="check">
              <input type="checkbox" [(ngModel)]="form.hasCheque" />
              <span>{{ t('finance.banks.fields.hasCheque') }}</span>
            </label>
            <label class="check">
              <input type="checkbox" [(ngModel)]="form.hasPos" />
              <span>{{ t('finance.banks.fields.hasPos') }}</span>
            </label>
            @if (form.hasPos) {
              <label>
                <span>{{ t('finance.banks.fields.posSerialNumber') }}</span>
                <input pInputText type="text" [(ngModel)]="form.posSerialNumber" />
              </label>
            }
            <label class="wide">
              <span>{{ t('finance.banks.fields.payableDocumentsAccountId') }}</span>
              <ain-financial-account-selector [(ngModel)]="form.payableDocumentsAccountId"></ain-financial-account-selector>
            </label>
            <label class="wide">
              <span>{{ t('finance.banks.fields.accountingAccountId') }}</span>
              <ain-financial-account-selector [(ngModel)]="form.accountingAccountId"></ain-financial-account-selector>
            </label>
            <label class="wide">
              <span>{{ t('finance.banks.fields.clearingInProcessAccountId') }}</span>
              <ain-financial-account-selector [(ngModel)]="form.clearingInProcessAccountId"></ain-financial-account-selector>
            </label>
            <label class="wide">
              <span>{{ t('finance.banks.fields.balanceAccountId') }}</span>
              <ain-financial-account-selector [(ngModel)]="form.balanceAccountId"></ain-financial-account-selector>
            </label>
          </div>
        </section>

        @if (errorMessage()) {
          <p class="message error">{{ errorMessage() }}</p>
        }
      }
    </ain-page>
  `,
  styles: [`
    .toolbar { margin-bottom: 12px; display: flex; justify-content: space-between; align-items: center; gap: 8px; }
    .back-link { color: var(--ain-color-primary); font-weight: 600; text-decoration: none; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; }
    .grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
    label { display: grid; gap: 6px; }
    .wide { grid-column: span 3; }
    label.check { grid-template-columns: auto 1fr; align-items: center; }
    .message.error { color: #b91c1c; }
    @media (max-width: 1200px) {
      .grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
      .wide { grid-column: span 2; }
    }
    @media (max-width: 760px) {
      .grid { grid-template-columns: 1fr; }
      .wide { grid-column: span 1; }
    }
  `]
})
export class FinanceBankDetailComponent implements OnInit {
  private readonly api = inject(FinanceApiService);
  private readonly localization = inject(LocalizationService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly errorMessage = signal('');
  readonly isCreateMode = signal(false);
  readonly current = signal<BankRecord | null>(null);

  readonly form = {
    bankName: '',
    branch: '',
    accountType: '',
    accountNumber: '',
    iban: '',
    cardNumber: '',
    hasCheque: false,
    hasPos: false,
    posSerialNumber: '',
    payableDocumentsAccountId: '',
    accountingAccountId: '',
    clearingInProcessAccountId: '',
    balanceAccountId: ''
  };

  ngOnInit(): void {
    const bankId = this.route.snapshot.paramMap.get('bankId');
    if (!bankId || bankId === 'new') {
      this.isCreateMode.set(true);
      this.loading.set(false);
      return;
    }

    this.api.getAccountingBank(bankId).subscribe({
      next: row => {
        this.current.set(row);
        this.patchForm(row);
        this.loading.set(false);
      },
      error: () => {
        this.errorMessage.set(this.t('common.loadFailed'));
        this.loading.set(false);
      }
    });
  }

  save(): void {
    this.errorMessage.set('');
    if (!this.form.bankName.trim()) {
      this.errorMessage.set(this.t('finance.banks.messages.invalidForm'));
      return;
    }

    this.saving.set(true);
    if (this.isCreateMode()) {
      const payload: CreateBankPayload = {
        number: '',
        idempotencyKey: this.createIdempotencyKey(),
        amount: 0,
        currencyCode: 'IRR',
        bankName: this.form.bankName.trim(),
        branch: this.form.branch.trim(),
        accountType: this.form.accountType.trim(),
        accountNumber: this.form.accountNumber.trim(),
        iban: this.form.iban.trim(),
        cardNumber: this.form.cardNumber.trim(),
        hasCheque: this.form.hasCheque,
        hasPos: this.form.hasPos,
        posSerialNumber: this.form.posSerialNumber.trim(),
        payableDocumentsAccountId: this.form.payableDocumentsAccountId.trim(),
        accountingAccountId: this.form.accountingAccountId.trim(),
        clearingInProcessAccountId: this.form.clearingInProcessAccountId.trim(),
        balanceAccountId: this.form.balanceAccountId.trim()
      };
      this.api.createAccountingBank(payload).subscribe({
        next: created => {
          this.saving.set(false);
          void this.router.navigate(['/financial/payments/banks', created.id]);
        },
        error: () => {
          this.errorMessage.set(this.t('finance.banks.messages.saveFailed'));
          this.saving.set(false);
        }
      });
      return;
    }

    const current = this.current();
    if (!current) {
      this.errorMessage.set(this.t('finance.banks.messages.noSelection'));
      this.saving.set(false);
      return;
    }

    const payload: UpdateBankPayload = {
      bankName: this.form.bankName.trim(),
      branch: this.form.branch.trim(),
      accountType: this.form.accountType.trim(),
      accountNumber: this.form.accountNumber.trim(),
      iban: this.form.iban.trim(),
      cardNumber: this.form.cardNumber.trim(),
      hasCheque: this.form.hasCheque,
      hasPos: this.form.hasPos,
      posSerialNumber: this.form.posSerialNumber.trim(),
      payableDocumentsAccountId: this.form.payableDocumentsAccountId.trim(),
      accountingAccountId: this.form.accountingAccountId.trim(),
      clearingInProcessAccountId: this.form.clearingInProcessAccountId.trim(),
      balanceAccountId: this.form.balanceAccountId.trim()
    };
    this.api.updateAccountingBank(current.id, payload).subscribe({
      next: updated => {
        this.current.set(updated);
        this.patchForm(updated);
        this.saving.set(false);
      },
      error: () => {
        this.errorMessage.set(this.t('finance.banks.messages.saveFailed'));
        this.saving.set(false);
      }
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private patchForm(row: BankRecord): void {
    this.form.bankName = row.bankName;
    this.form.branch = row.branch;
    this.form.accountType = row.accountType;
    this.form.accountNumber = row.accountNumber;
    this.form.iban = row.iban;
    this.form.cardNumber = row.cardNumber;
    this.form.hasCheque = row.hasCheque;
    this.form.hasPos = row.hasPos;
    this.form.posSerialNumber = row.posSerialNumber;
    this.form.payableDocumentsAccountId = row.payableDocumentsAccountId;
    this.form.accountingAccountId = row.accountingAccountId;
    this.form.clearingInProcessAccountId = row.clearingInProcessAccountId;
    this.form.balanceAccountId = row.balanceAccountId;
  }

  private createIdempotencyKey(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID().replace(/-/g, '');
    }

    return `${Date.now()}${Math.floor(Math.random() * 1000000)}`;
  }
}
